import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import React, { useMemo } from "react";
import getInternalStyle from "../../../utils/Styles";
import MessageTemplates from "../components/messages/templates/MessageTemplates";
import TemplateAnchor from "../components/messages/templates/TemplateAnchor";
import TypingIndicator from "../components/messages/TypingIndicator";
import BotMessageBox from "../components/messages/BotMessageBox";
import MessageMetadata from "../components/messages/MessageMetadata";
import MessageFeedback from "../components/messages/MessageFeedback";
import InjectedText from "../components/messages/InjectedText";
import WelcomeMessage from "../components/messages/welcome/WelcomeMessage";
import ChatService from "../../../front-services/ChatService";
import {
  botMarkdownComponents,
  GuestLinkRenderer,
  widgetUrlTransform,
} from "../components/messages/mapping/CustomLinkRenderer";
import remarkContactAutolink from "../components/messages/mapping/remarkContactAutolink";
import SensitiveAlertsBanner from "../components/messages/SensitiveAlertsBanner";
import QuotePills from "../components/messages/QuotePills";

/** Bot copy only — a guest's own phone number is not a control surface. */
const botRemarkPlugins = [remarkBreaks, remarkContactAutolink];

interface ChatMessagesProps {
  messages: any[];
  style: any;
  isMobile: boolean;
  assistantChatConfiguration: any;
  welcomeMessageTime: string;
  isMessageLoading: boolean;
  toolName: string | null;
  setFullScreen: (state: any) => void;
  handleShortcutClick: (message: string) => void;
  handleQuotePillClick: (message: string) => void;
  showHandoffButton: boolean;
  language: string;
  chatMessagesRef: React.Ref<HTMLDivElement>;
  lastMessageRef?: React.Ref<HTMLDivElement>;
  assistantId: string;
  threadId: string | null;
}

export const ChatMessages = ({
  messages,
  style,
  isMobile,
  assistantChatConfiguration,
  welcomeMessageTime,
  isMessageLoading,
  toolName,
  setFullScreen,
  handleShortcutClick,
  handleQuotePillClick,
  showHandoffButton,
  language,
  chatMessagesRef,
  lastMessageRef,
  assistantId,
  threadId,
}: ChatMessagesProps) => {
  const styles = getInternalStyle(assistantId, style);

  // Bot copy gets the classifying renderer (booking CTA vs email/phone/
  // WhatsApp chip vs plain inline link); guest copy deliberately does not.
  const botComponents = useMemo(
    () => botMarkdownComponents({ assistantId, language, style }),
    [assistantId, language, style]
  );

  return (
    <div className="widget-chat-messages" ref={chatMessagesRef}>
      {/* An opened chat must never greet with silence (auto-open especially):
          when no welcome message is configured, WelcomeMessage falls back to
          a localized default. */}
      {(assistantChatConfiguration.welcomeMessage ||
        assistantChatConfiguration.loaded) && (
        <BotMessageBox
          assistantId={assistantId}
          style={style}
          getInternalStyle={getInternalStyle}
          language={language}
        >
          <WelcomeMessage
            style={style}
            assistantChatConfiguration={assistantChatConfiguration}
            handleShortcutClick={handleShortcutClick}
            assistantId={assistantId}
            welcomeMessageTime={welcomeMessageTime}
            language={language}
            isMobile={isMobile}
          />
        </BotMessageBox>
      )}
      {messages.map((message, index) => {
        const isLast = index === messages.length - 1;
        return (
          <div key={index}>
            {message.type !== "user" ? (
              <BotMessageBox
                assistantId={assistantId}
                style={style}
                getInternalStyle={getInternalStyle}
                language={language}
              >
                <div
                  style={{
                    marginBottom: "10px",
                    backgroundColor: styles.messages.bot.color,
                  }}
                  ref={isLast ? lastMessageRef : null}
                  className="widget-chatbot-message"
                >
                  <ReactMarkdown
                    remarkPlugins={botRemarkPlugins}
                    urlTransform={widgetUrlTransform}
                    components={botComponents}
                  >
                    {message.content}
                  </ReactMarkdown>

                  {message.sensitive_alerts && (
                    <SensitiveAlertsBanner alerts={message.sensitive_alerts} />
                  )}

                  {message.templates && (
                    <MessageTemplates
                      templates={message.templates}
                      isMobile={isMobile}
                      assistantId={assistantId}
                      threadId={threadId}
                    />
                  )}

                  {/* Only on the last message: the pills answer THIS question,
                      and any next message (the tap's own, or something the guest
                      typed instead) retires them with no state to keep in sync. */}
                  {isLast && message.quotePills && (
                    <QuotePills
                      pills={message.quotePills}
                      language={language}
                      onPillClick={handleQuotePillClick}
                    />
                  )}

                  {message.sliderTemplates && (
                    <TemplateAnchor
                      templates={message.sliderTemplates}
                      assistantId={assistantId}
                      threadId={threadId}
                      isMobile={isMobile}
                      language={language}
                    />
                  )}

                  {message.metadata && (
                    <MessageMetadata
                      metadata={message.metadata}
                      style={style}
                      isMobile={isMobile}
                      setFullScreen={setFullScreen}
                      showHandoffButton={showHandoffButton}
                      language={language}
                      assistantChatConfiguration={assistantChatConfiguration}
                    />
                  )}
                  <div className="msg-meta-row">
                    <MessageFeedback
                      message={message}
                      index={index}
                      assistantId={assistantId}
                      language={language}
                    />
                    <p className="message-time">{message.time}</p>
                  </div>
                </div>
              </BotMessageBox>
            ) : (
              <div
                style={{
                  marginBottom: "10px",
                  backgroundColor: styles.messages.user.color,
                }}
                className={`widget-user-message${
                  message.injected ? " is-injected" : ""
                }`}
              >
                {message.injected ? (
                  <InjectedText text={message.content} />
                ) : (
                  <ReactMarkdown
                    remarkPlugins={[remarkBreaks]}
                    urlTransform={widgetUrlTransform}
                    components={{
                      a: GuestLinkRenderer,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}

                <p className="message-time">{message.time}</p>
              </div>
            )}
          </div>
        );
      })}
      {isMessageLoading && (
        <BotMessageBox
          assistantId={assistantId}
          style={style}
          getInternalStyle={getInternalStyle}
          language={language}
        >
          <div className="bot-message-content">
            <div
              className="widget-chatbot-message"
              style={{
                backgroundColor: styles.messages.bot.color,
              }}
            >
              <TypingIndicator
                toolName={
                  ChatService.isIodah(assistantId) && toolName === "hotel_info"
                    ? "hotel_info_iodah"
                    : toolName
                }
                language={language}
                onDark={!ChatService.isHM(assistantId)}
              />
            </div>
          </div>
        </BotMessageBox>
      )}
    </div>
  );
};
